import { useMemo, useState } from "react";
import { Image, StyleSheet, TextInput, View } from "react-native";
import { Button, Checkbox, IconButton, RadioButton, Text } from "react-native-paper";

import { SignatureModal } from "./SignatureModal";
import type { QuestionComponentProps } from "./types";

import { logRocketRedactProps } from "@/lib/logrocket";
import { colors } from "@/lib/theme";

const UNSIGNED_PREFIX = "__unsigned__:";

const PATIENT_REFUSAL_REASONS = [
    "Patient unable to sign",
    "Patient refused to sign",
    "Verbal consent given by patient/family"
] as const;

export function SignatureQuestion({
    question,
    value,
    onChange,
    readOnly,
    onFocus,
    childQuestions,
    childAnswers,
    onChildAnswerChange
}: QuestionComponentProps) {
    const [modalVisible, setModalVisible] = useState(false);

    const isSigned = !!value && value.startsWith("data:");
    const isUnsigned = !!value && value.startsWith(UNSIGNED_PREFIX);
    const unsignedReason = isUnsigned ? value.slice(UNSIGNED_PREFIX.length) : "";

    const type = question.type as "patientSignature" | "employeeSignature" | "customSignature";

    // Patient signature: "not signing" checkbox state
    const [notSigning, setNotSigning] = useState(isUnsigned);
    const [selectedReason, setSelectedReason] = useState<string>(() => {
        if (!isUnsigned) {
            return "";
        }
        // Check if it's a preset reason
        for (const r of PATIENT_REFUSAL_REASONS) {
            if (unsignedReason === r) {
                return r;
            }
        }
        // Must be "Other:..."
        if (unsignedReason.startsWith("Other:")) {
            return "Other";
        }
        return "";
    });
    const [otherText, setOtherText] = useState(() => {
        if (isUnsigned && unsignedReason.startsWith("Other:")) {
            return unsignedReason.slice("Other:".length);
        }
        return "";
    });

    // Custom signature: find child question for signer name
    const childQuestion = useMemo(() => {
        if (type !== "customSignature" || !childQuestions) {
            return null;
        }
        return (
            childQuestions.find((cq) => String(cq.parentQuestionId) === String(question.id)) ?? null
        );
    }, [type, childQuestions, question.id]);

    const showNameField =
        type === "customSignature" && childQuestion && !question.config?.hideNameFieldSignature;

    // --- Handlers ---

    const handleDone = (base64: string) => {
        onChange(base64);
        setModalVisible(false);
        setNotSigning(false);
    };

    const handleClear = () => {
        onChange("");
    };

    const handleNotSigningToggle = () => {
        const next = !notSigning;
        setNotSigning(next);
        if (!next) {
            // Unchecked — clear unsigned value
            onChange("");
            setSelectedReason("");
            setOtherText("");
        }
    };

    const handleReasonChange = (reason: string) => {
        setSelectedReason(reason);
        if (reason === "Other") {
            onChange(UNSIGNED_PREFIX + "Other:" + otherText);
        } else {
            onChange(UNSIGNED_PREFIX + reason);
        }
    };

    const handleOtherTextChange = (text: string) => {
        setOtherText(text);
        onChange(UNSIGNED_PREFIX + "Other:" + text);
    };

    // --- Signature preview (shared across all types) ---

    const renderPreview = () => {
        if (isSigned) {
            return (
                <View style={styles.previewContainer}>
                    <Image
                        {...logRocketRedactProps()}
                        source={{ uri: value }}
                        style={styles.previewImage}
                        resizeMode="contain"
                    />
                    {!readOnly && (
                        <IconButton
                            icon="close-circle"
                            size={22}
                            onPress={handleClear}
                            style={styles.clearBtn}
                        />
                    )}
                </View>
            );
        }

        if (isUnsigned) {
            return (
                <View style={styles.pad}>
                    <Text style={styles.unsignedText}>Not signed: {unsignedReason}</Text>
                </View>
            );
        }

        if (readOnly) {
            return (
                <View style={styles.pad}>
                    <Text style={styles.placeholder}>No signature</Text>
                </View>
            );
        }

        return (
            <Button
                mode="outlined"
                icon="draw"
                onPress={() => setModalVisible(true)}
                style={styles.tapToSign}
                contentStyle={styles.tapToSignContent}
            >
                Tap to sign
            </Button>
        );
    };

    // --- Patient "not signing" flow ---

    const renderPatientNotSigning = () => {
        if (type !== "patientSignature") {
            return null;
        }
        if (!question.config?.allowUnsignedSignature) {
            return null;
        }
        if (readOnly) {
            return null;
        }
        if (isSigned) {
            return null;
        }

        return (
            <View style={styles.notSigningSection}>
                <Checkbox.Item
                    label="Patient is not signing"
                    status={notSigning ? "checked" : "unchecked"}
                    onPress={handleNotSigningToggle}
                    style={styles.checkboxItem}
                    labelStyle={styles.checkboxLabel}
                    position="leading"
                />
                {notSigning && (
                    <View style={styles.reasonsContainer}>
                        {PATIENT_REFUSAL_REASONS.map((reason) => (
                            <RadioButton.Item
                                key={reason}
                                label={reason}
                                value={reason}
                                status={selectedReason === reason ? "checked" : "unchecked"}
                                onPress={() => handleReasonChange(reason)}
                                style={styles.radioItem}
                                labelStyle={styles.radioLabel}
                            />
                        ))}
                        <RadioButton.Item
                            label="Other"
                            value="Other"
                            status={selectedReason === "Other" ? "checked" : "unchecked"}
                            onPress={() => handleReasonChange("Other")}
                            style={styles.radioItem}
                            labelStyle={styles.radioLabel}
                        />
                        {selectedReason === "Other" && (
                            <TextInput
                                style={styles.otherInput}
                                placeholder="Enter reason..."
                                value={otherText}
                                onFocus={onFocus}
                                onChangeText={handleOtherTextChange}
                            />
                        )}
                    </View>
                )}
            </View>
        );
    };

    // --- Custom signature: signer name field ---

    const renderNameField = () => {
        if (!showNameField || !childQuestion) {
            return null;
        }
        const nameValue = childAnswers?.[childQuestion.id] ?? "";

        return (
            <View style={styles.nameFieldContainer}>
                <Text style={styles.nameLabel}>{childQuestion.text}</Text>
                <TextInput
                    style={styles.nameInput}
                    placeholder="Enter signer's full name"
                    value={nameValue}
                    onFocus={onFocus}
                    onChangeText={(text) => onChildAnswerChange?.(childQuestion.id, text)}
                    editable={!readOnly}
                />
            </View>
        );
    };

    return (
        <View style={styles.container}>
            {renderNameField()}
            {(!notSigning || isSigned) && renderPreview()}
            {renderPatientNotSigning()}

            <SignatureModal
                visible={modalVisible}
                onDone={handleDone}
                onClose={() => setModalVisible(false)}
                title={question.text}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        gap: 8
    },
    pad: {
        height: 80,
        borderWidth: 1,
        borderColor: "#ddd",
        borderRadius: 8,
        backgroundColor: "#fafafa",
        justifyContent: "center",
        alignItems: "center"
    },
    placeholder: {
        color: "#999",
        fontStyle: "italic"
    },
    unsignedText: {
        color: colors.textSecondary,
        fontStyle: "italic",
        fontSize: 13
    },
    previewContainer: {
        position: "relative",
        borderWidth: 1,
        borderColor: "#ddd",
        borderRadius: 8,
        backgroundColor: "#fff",
        overflow: "hidden"
    },
    previewImage: {
        width: "100%",
        height: 100
    },
    clearBtn: {
        position: "absolute",
        top: -4,
        right: -4
    },
    tapToSign: {
        borderStyle: "dashed",
        borderRadius: 8
    },
    tapToSignContent: {
        paddingVertical: 16
    },
    notSigningSection: {
        marginTop: 4
    },
    checkboxItem: {
        paddingLeft: 0
    },
    checkboxLabel: {
        fontSize: 14
    },
    reasonsContainer: {
        marginLeft: 8,
        gap: 0
    },
    radioItem: {
        paddingVertical: 4
    },
    radioLabel: {
        fontSize: 13
    },
    otherInput: {
        borderWidth: 1,
        borderColor: "#ddd",
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        marginLeft: 40,
        marginTop: 4,
        fontSize: 14
    },
    nameFieldContainer: {
        gap: 4
    },
    nameLabel: {
        fontSize: 13,
        color: colors.textSecondary,
        fontWeight: "500"
    },
    nameInput: {
        borderWidth: 1,
        borderColor: "#ddd",
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        fontSize: 14,
        backgroundColor: "#fff"
    }
});
